import { useCallback, useMemo } from "react";
import { Check, ChevronDown, Filter, Mail, Sparkles } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";

import {
  getNotificationSidebarOptions,
} from "../constants/config";
import { SURFACE_BACK_BTN_CLS } from "../constants/layout";
import type { NotificationFilter, MappedNotificationCounts } from "../types";

export function NotificationFilterSelector({
  activeFilter,
  mappedCounts,
  isAccountView,
  onSelectFilter,
}: {
  activeFilter: NotificationFilter;
  mappedCounts: MappedNotificationCounts;
  isAccountView: boolean;
  onSelectFilter: (filter: NotificationFilter) => void;
}) {
  const options = getNotificationSidebarOptions(isAccountView);

  const countForValue = (value: string): number => {
    if (value === "all") return mappedCounts.total;
    if (value === "in-progress" || value === "failed" || value === "completed" || value === "cancelled") {
      return mappedCounts.byStatus[value as keyof typeof mappedCounts.byStatus] ?? 0;
    }
    return mappedCounts.byCategory[value] ?? 0;
  };

  const statusOptions = options.filter(
    (o) => o.group === "status",
  );
  const categoryOptions = options.filter(
    (o) => o.group === "task" || o.group === "alert",
  );

  const { activeStatuses, activeCategories, isAll, isUnread, isNotable } = useMemo(() => {
    if (activeFilter.kind === "all") {
      return { activeStatuses: [] as string[], activeCategories: [] as string[], isAll: true, isUnread: false, isNotable: false };
    }
    if (activeFilter.kind === "unread") {
      return { activeStatuses: [] as string[], activeCategories: [] as string[], isAll: false, isUnread: true, isNotable: false };
    }
    if (activeFilter.kind === "notable") {
      return { activeStatuses: [] as string[], activeCategories: [] as string[], isAll: false, isUnread: false, isNotable: true };
    }
    if (activeFilter.kind === "status") {
      return { activeStatuses: [activeFilter.status], activeCategories: [] as string[], isAll: false, isUnread: false, isNotable: false };
    }
    if (activeFilter.kind === "category") {
      return { activeStatuses: [] as string[], activeCategories: [activeFilter.category], isAll: false, isUnread: false, isNotable: false };
    }
    if (activeFilter.kind === "multi") {
      return { activeStatuses: activeFilter.statuses, activeCategories: activeFilter.categories, isAll: false, isUnread: false, isNotable: false };
    }
    return { activeStatuses: [] as string[], activeCategories: [] as string[], isAll: true, isUnread: false, isNotable: false };
  }, [activeFilter]);

  const selectionCount = activeStatuses.length + activeCategories.length;

  const toggleStatus = useCallback((status: string) => {
    const newStatuses = activeStatuses.includes(status)
      ? activeStatuses.filter((s) => s !== status)
      : [...activeStatuses, status];
    if (newStatuses.length === 0 && activeCategories.length === 0) {
      onSelectFilter({ kind: "all" });
    } else if (newStatuses.length === 1 && activeCategories.length === 0) {
      onSelectFilter({ kind: "status", status: newStatuses[0] as any });
    } else {
      onSelectFilter({ kind: "multi", statuses: newStatuses, categories: activeCategories });
    }
  }, [activeStatuses, activeCategories, onSelectFilter]);

  const toggleCategory = useCallback((category: string) => {
    const newCategories = activeCategories.includes(category)
      ? activeCategories.filter((c) => c !== category)
      : [...activeCategories, category];
    if (newCategories.length === 0 && activeStatuses.length === 0) {
      onSelectFilter({ kind: "all" });
    } else if (newCategories.length === 1 && activeStatuses.length === 0) {
      onSelectFilter({ kind: "category", category: newCategories[0] });
    } else {
      onSelectFilter({ kind: "multi", statuses: activeStatuses, categories: newCategories });
    }
  }, [activeStatuses, activeCategories, onSelectFilter]);

  const selectAll = useCallback(() => {
    onSelectFilter({ kind: "all" });
  }, [onSelectFilter]);

  const selectUnread = useCallback(() => {
    onSelectFilter({ kind: "unread" });
  }, [onSelectFilter]);

  const selectNotable = useCallback(() => {
    onSelectFilter({ kind: "notable" });
  }, [onSelectFilter]);

  const triggerLabel = isAll
    ? "All"
    : isUnread
      ? "Unread"
      : isNotable
        ? "Notable"
        : `${selectionCount} selected`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(SURFACE_BACK_BTN_CLS, "shrink-0")}
          aria-label={`Filter: ${triggerLabel}`}
        >
          <Filter className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-60 overflow-hidden rounded-xl border-border/60 p-0 shadow-xl"
      >
        <div className="max-h-[320px] overflow-y-auto">
          <div className="px-3 pt-3 pb-2">
            <p className="mb-1.5 px-2 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Filter this view
            </p>
            <FilterOption
              icon={options[0].icon}
              label="All"
              count={countForValue("all")}
              isActive={isAll}
              onSelect={selectAll}
            />
            <FilterOption
              icon={Mail}
              label="Unread"
              count={mappedCounts.unread}
              isActive={isUnread}
              onSelect={selectUnread}
            />
            <FilterOption
              icon={Sparkles}
              label="Notable"
              count={mappedCounts.notable}
              isActive={isNotable}
              onSelect={selectNotable}
            />
          </div>

          {statusOptions.length > 0 ? (
            <div className="border-t border-border/40 px-3 pb-2 pt-2.5">
              <p className="mb-1.5 px-2 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Status
              </p>
              {statusOptions.map((option) => (
                <FilterOption
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  count={countForValue(option.value)}
                  isActive={activeStatuses.includes(option.value)}
                  onSelect={() => toggleStatus(option.value)}
                />
              ))}
            </div>
          ) : null}

          {categoryOptions.length > 0 ? (
            <div className="border-t border-border/40 px-3 pb-3 pt-2.5">
              <p className="mb-1.5 px-2 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Categories
              </p>
              {categoryOptions.map((option) => (
                <FilterOption
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  count={countForValue(option.value)}
                  isActive={activeCategories.includes(option.value)}
                  onSelect={() => toggleCategory(option.value)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FilterOption({
  icon: Icon,
  label,
  isActive,
  count,
  onSelect,
}: {
  icon: any;
  label: string;
  isActive: boolean;
  count: number;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] font-medium transition-colors",
        isActive
          ? "text-foreground"
          : "text-foreground/80 hover:bg-accent/50 hover:text-foreground",
      )}
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-accent">
        <Icon className="size-3.5 opacity-95" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <span className="text-[11px] tabular-nums text-muted-foreground">{count}</span>
      {isActive ? (
        <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
      ) : (
        <span className="size-3.5 shrink-0" />
      )}
    </button>
  );
}
